import { useContext, useState, useEffect } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SupportService from '../../../services/Support';
import { dateTimeFormat } from '../../../helpers/dateFormat';
import FormattedSupportText from './FormattedSupportText';
import getFormatByMimetypes from '../../../helpers/getFormatByMimetypes';
import SupportAttachments from './SupportAttachments';

const TRELLO_ATTACHMENT_ID_IN_PATH = /\/attachments\/([a-f0-9]{24})\//gi;
const MARKDOWN_LINK = /!?\[([^\]]*)]\(\s*((?:https?:\/\/|\/)[^\s)]+)\s*(?:"[^"]*")?\s*\)/gi;
const RAW_URL = /https?:\/\/[^\s)]+/gi;
const HEX24 = /[a-f0-9]{24}/gi;

const normalizeComparableUrl = (raw: string) => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  const noHashQuery = trimmed.split('#')[0].split('?')[0];
  try {
    return new URL(noHashQuery).href;
  } catch {
    return noHashQuery;
  }
};

const buildMarkdownLinkLabelByUrl = (description: string | undefined) => {
  const map = new Map<string, string>();
  if (!description) return map;
  const re = new RegExp(MARKDOWN_LINK.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(description)) !== null) {
    const key = normalizeComparableUrl(match[2]);
    const label = (match[1] || '').trim();
    if (key && label && !map.has(key)) {
      map.set(key, label);
    }
  }
  return map;
};

const extractAttachmentTokens = (attachments: { url?: string; name?: string }[]) => {
  const tokens = new Set<string>();
  for (const attachment of attachments) {
    const values = [attachment.url || '', attachment.name || ''];
    for (const value of values) {
      if (!value) continue;
      const lower = value.toLowerCase();
      const hexMatches = lower.match(HEX24) || [];
      for (const hex of hexMatches) {
        tokens.add(hex);
      }
      const fromPath = value.split('/').filter(Boolean).pop();
      if (fromPath) {
        tokens.add(decodeURIComponent(fromPath).toLowerCase());
      }
    }
  }
  return tokens;
};

const urlLooksLikeAttachment = (url: string, attachmentTokens: Set<string>, attachmentKeys: Set<string>) => {
  const normalized = normalizeComparableUrl(url);
  if (attachmentKeys.has(normalized)) return true;
  const lowerUrl = url.toLowerCase();
  for (const token of attachmentTokens) {
    if (token && lowerUrl.includes(token)) return true;
  }
  return false;
};

const stripAttachmentDuplicatesFromDescription = (description: string, attachments: { url?: string }[]) => {
  if (!description) return '';
  if (!attachments.length) return description;

  const attachmentKeys = new Set(
    attachments.map((a) => normalizeComparableUrl(a.url || '')).filter((k) => k.length > 0),
  );
  if (!attachmentKeys.size) return description;
  const attachmentTokens = extractAttachmentTokens(attachments);

  let text = description;
  const re = new RegExp(MARKDOWN_LINK.source, 'gi');
  const fragments: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = re.exec(description)) !== null) {
    if (urlLooksLikeAttachment(match[2], attachmentTokens, attachmentKeys)) {
      fragments.push(match[0]);
    }
  }
  for (const fragment of fragments) {
    text = text.split(fragment).join('');
  }

  const urls = text.match(RAW_URL) || [];
  for (const url of urls) {
    if (urlLooksLikeAttachment(url, attachmentTokens, attachmentKeys)) {
      text = text.split(url).join('');
    }
  }

  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('\n')
    .trim();
};

const attachmentDisplayName = (
  attachment: { url?: string; name?: string; mimetype?: string },
  labelByUrl: Map<string, string>,
) => {
  const key = normalizeComparableUrl(attachment.url || '');
  const fromMarkdown = key ? labelByUrl.get(key) : '';
  if (fromMarkdown) return fromMarkdown;
  let urlPath: string | undefined;
  try {
    urlPath = new URL(attachment.url || '').pathname.split('/').filter(Boolean).pop();
  } catch {
    urlPath = undefined;
  }
  if (urlPath) return decodeURIComponent(urlPath);
  const fromName = attachment.name?.split('/').pop();
  if (fromName) return fromName;
  return `attachment.${getFormatByMimetypes(attachment.mimetype || '')}`;
};

const collectTrelloAttachmentIdsFromText = (text: string | undefined): string[] => {
  if (!text) return [];
  const ids = new Set<string>();
  let match: RegExpExecArray | null;
  const re = new RegExp(TRELLO_ATTACHMENT_ID_IN_PATH.source, 'gi');
  while ((match = re.exec(text)) !== null) {
    ids.add(match[1]);
  }
  return Array.from(ids);
};

const attachmentMatchesRef = (attachment: { name?: string; url?: string }, ref: string) => {
  const name = attachment.name || '';
  const url = attachment.url || '';
  const base = name.split('/').pop() || '';
  return name === ref || base === ref || url.includes(ref) || name.includes(ref);
};

const attachmentsForReply = (reply: any) => {
  const all = Array.isArray(reply.attachments) ? reply.attachments : [];
  const refs: string[] | undefined = reply.attachmentRefs;
  if (Array.isArray(refs) && refs.length > 0) {
    const byRefs = all.filter((a: any) => refs.some((ref) => attachmentMatchesRef(a, ref)));
    if (byRefs.length > 0) return byRefs;
  }
  const trelloIds = collectTrelloAttachmentIdsFromText(reply.description);
  if (trelloIds.length > 0) {
    const byTrello = all.filter((a: any) =>
      trelloIds.some((id) => (a.name && String(a.name).includes(id)) || (a.url && String(a.url).includes(id))),
    );
    if (byTrello.length > 0) return byTrello;
  }
  return all;
};

type SupportReplyListProps = {
  refresh: boolean;
  ticketUserId?: string;
};

const SupportReplyList = ({ refresh, ticketUserId }: SupportReplyListProps) => {
  const { token }: any = useContext(AuthContext);
  const [data, setData] = useState([] as any[]);
  const { id } = useParams<{ id: string }>();

  const getSupportReplyById = async () => {
    try {
      const result = await SupportService(token).listReply(id);
      const dataResult: any = result.data;
      setData(dataResult);
    } catch (e: any) {
      toast.error('Error: ' + id);
    }
  };

  useEffect(() => {
    getSupportReplyById();
  }, []);

  useEffect(() => {
    getSupportReplyById();
  }, [refresh]);

  return (
    <div className="d-flex flex-column">
      {data.map((r, index) => {
        const authorName = `${r?.user?.firstName || ''} ${r?.user?.lastName || ''}`.trim() || 'Support';
        const isTicketAuthor =
          ticketUserId !== undefined &&
          String(r?.user?.id ?? '') !== '' &&
          String(r?.user?.id) === String(ticketUserId);
        const replyAttachments = attachmentsForReply(r);
        const markdownLabelByUrl = buildMarkdownLinkLabelByUrl(r.description);
        const replyDescriptionForDisplay =
          replyAttachments.length > 0
            ? stripAttachmentDuplicatesFromDescription(r.description || '', replyAttachments)
            : r.description;
        return (
          <div
            key={`item-${r.id || index}`}
            className={`d-flex w-100 mb-3 ${isTicketAuthor ? 'justify-content-end' : 'justify-content-start'}`}
          >
            <div
              className={`rounded-lg p-3 w-75 ${
                isTicketAuthor ? 'bg-primary text-white' : 'bg-white border shadow-sm'
              }`}
            >
              <div className={`small mb-2 ${isTicketAuthor ? 'text-white' : 'text-muted'}`}>
                <span className="font-weight-bold">{authorName}</span>
                {r.createdAt && (
                  <>
                    <span className="mx-1">·</span>
                    <span>{dateTimeFormat(r.createdAt)}</span>
                  </>
                )}
              </div>
              <div className={isTicketAuthor ? 'text-white' : ''}>
                <FormattedSupportText text={replyDescriptionForDisplay} />
              </div>
              {replyAttachments.length > 0 && (
                <div className="mt-3">
                  <SupportAttachments
                    attachments={replyAttachments}
                    resolveName={(attachment) => attachmentDisplayName(attachment, markdownLabelByUrl)}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SupportReplyList;
